import { config } from '@/config/environment'

type LogLevel = 'error' | 'warn' | 'info' | 'debug'

interface LogEntry {
  level: LogLevel
  message: string
  timestamp: string
  environment: string
  args?: any[]
  stack?: string
  userAgent?: string
  url?: string
}

class Logger {
  private level: LogLevel
  private buffer: LogEntry[] = []
  private maxBufferSize = 100
  
  constructor(level: LogLevel = config.logLevel) {
    this.level = level
  }
  
  private shouldLog(level: LogLevel): boolean {
    const levels: LogLevel[] = ['error', 'warn', 'info', 'debug']
    const currentIndex = levels.indexOf(this.level)
    const messageIndex = levels.indexOf(level)
    return messageIndex <= currentIndex
  }
  
  private createLogEntry(level: LogLevel, message: string, args?: any[]): LogEntry {
    return {
      level,
      message,
      timestamp: new Date().toISOString(),
      environment: config.environment,
      args,
      userAgent: navigator.userAgent,
      url: window.location.href
    }
  }
  
  private addToBuffer(entry: LogEntry): void {
    this.buffer.push(entry)
    if (this.buffer.length > this.maxBufferSize) {
      this.buffer.shift() // Remove oldest entry
    }
  }
  
  error(message: string, ...args: any[]): void {
    if (this.shouldLog('error')) {
      const entry = this.createLogEntry('error', message, args)
      entry.stack = new Error().stack
      
      console.error(`[ERROR] ${message}`, ...args)
      this.addToBuffer(entry)
      
      // Send to monitoring service in production
      if (config.environment === 'production') {
        this.sendToMonitoring(entry)
      }
    }
  }
  
  warn(message: string, ...args: any[]): void {
    if (this.shouldLog('warn')) {
      const entry = this.createLogEntry('warn', message, args)
      console.warn(`[WARN] ${message}`, ...args)
      this.addToBuffer(entry)
    }
  }
  
  info(message: string, ...args: any[]): void {
    if (this.shouldLog('info')) {
      const entry = this.createLogEntry('info', message, args)
      console.info(`[INFO] ${message}`, ...args)
      this.addToBuffer(entry)
    }
  }
  
  debug(message: string, ...args: any[]): void {
    if (this.shouldLog('debug')) {
      const entry = this.createLogEntry('debug', message, args)
      console.log(`[DEBUG] ${message}`, ...args)
      this.addToBuffer(entry)
    }
  }
  
  // Special methods for common fraud detection events
  fraudDetected(leadId: string, riskScore: number, patterns: string[]): void {
    this.warn(`Fraud detected for lead ${leadId}`, {
      riskScore,
      patterns,
      action: 'blocked'
    })
  }
  
  apiError(endpoint: string, error: any, statusCode?: number): void {
    this.error(`API error on ${endpoint}`, {
      error: error.message || error,
      statusCode,
      endpoint
    })
  }
  
  performanceLog(operation: string, duration: number, details?: any): void {
    if (duration > 1000) { // Log slow operations
      this.warn(`Slow operation: ${operation} took ${duration}ms`, details)
    } else {
      this.debug(`Performance: ${operation} took ${duration}ms`, details)
    }
  }
  
  userAction(action: string, details?: any): void {
    this.info(`User action: ${action}`, details)
  }
  
  private sendToMonitoring(entry: LogEntry): void {
    // Integration with monitoring services (Sentry, LogRocket, etc.)
    // This would be implemented based on your monitoring service
    try {
      // Example: Send to a monitoring endpoint
      fetch('/api/v1/monitoring/logs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entry)
      }).catch(() => {
        // Silently fail to avoid infinite loops
      })
    } catch (error) {
      // Silently fail
    }
  }
  
  // Export logs for debugging
  exportLogs(): LogEntry[] {
    return [...this.buffer]
  }
  
  // Clear log buffer
  clearLogs(): void {
    this.buffer = []
  }
  
  // Change log level dynamically (useful for debugging)
  setLevel(level: LogLevel): void {
    this.level = level
    this.info(`Log level changed to ${level}`)
  }
  
  // Get current stats
  getStats(): { level: LogLevel; bufferSize: number; environment: string } {
    return {
      level: this.level,
      bufferSize: this.buffer.length,
      environment: config.environment
    }
  }
}

export const logger = new Logger()

// Export for development debugging
if (config.debugMode && typeof window !== 'undefined') {
  ;(window as any).logger = logger
}